import React, { useEffect, useMemo, useRef, useState } from "react";
import "./WeddingHomeComponent.scss";
import CardComponent from "../CardComponent/CardComponent";
import AgendaComponent from "../AgendaComponent/AgendaComponent";
import PhotoComponent from "../PhotoComponent/PhotoComponent";
import CountDownComponent from "../CountDownComponent/CountDownComponent";
import RsvpComponent from "../RsvpComponent/RsvpComponent";
import TableSearch from "../TableSearch/TableSearch";
import MessageToGuests from "../MessageToGuests/MessageToGuests";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import { useNavigate } from "react-router-dom";
import WeddingFooterComponent from "../WeddingFooterComponent/WeddingFooterComponent";
import { FaMusic, FaPause, FaVolumeMute } from "react-icons/fa";

import weddingMusicFile from "./wedding-music.mp3";
import OpeningComponent from "../OpeningComponent/OpeningComponent";
import InviteSectionComponent from "../InviteSectionComponent/InviteSectionComponent";
import HeroSectionComponent from "../HeroSectionComponent/HeroSectionComponent";

const DEFAULT_THEME = {
  primary: "#C9A574",
  secondary: "#F2E5C6",
  accent: "#A48354",
  surface: "#FFFFFF",
  text: "#221C01",
};

const normalizeHex = (value, fallback = "#000000") => {
  if (!value) return fallback;

  let hex = String(value).trim();

  if (!hex.startsWith("#")) {
    hex = `#${hex}`;
  }

  if (/^#([0-9A-Fa-f]{3})$/.test(hex)) {
    const short = hex.replace("#", "");
    return `#${short
      .split("")
      .map((char) => char + char)
      .join("")}`.toUpperCase();
  }

  if (/^#([0-9A-Fa-f]{6})$/.test(hex)) {
    return hex.toUpperCase();
  }

  return fallback;
};

const sanitizeTheme = (theme = {}) => ({
  primary: normalizeHex(theme.primary, DEFAULT_THEME.primary),
  secondary: normalizeHex(theme.secondary, DEFAULT_THEME.secondary),
  accent: normalizeHex(theme.accent, DEFAULT_THEME.accent),
  surface: normalizeHex(theme.surface, DEFAULT_THEME.surface),
  text: normalizeHex(theme.text, DEFAULT_THEME.text),
});

const hexToRgb = (hex) => {
  const safeHex = normalizeHex(hex).replace("#", "");
  const bigint = parseInt(safeHex, 16);

  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

const getLuminance = ({ r, g, b }) => {
  const channels = [r, g, b].map((value) => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};

const getContrastText = (hex) => {
  const luminance = getLuminance(hexToRgb(hex));
  return luminance > 0.55 ? "#221C01" : "#FFFFFF";
};

const createThemeStyle = (theme = {}) => {
  const safeTheme = sanitizeTheme(theme);

  return {
    "--theme-primary": safeTheme.primary,
    "--theme-secondary": safeTheme.secondary,
    "--theme-accent": safeTheme.accent,
    "--theme-surface": safeTheme.surface,
    "--theme-text": safeTheme.text,
    "--theme-on-primary": getContrastText(safeTheme.primary),
    "--theme-on-secondary": getContrastText(safeTheme.secondary),
    "--theme-on-accent": getContrastText(safeTheme.accent),
  };
};

const isSectionVisible = (weddingData, key) => {
  return weddingData?.[key] !== false;
};

const WeddingHomeComponent = ({ weddingData = null, slug = null }) => {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const introTimerRef = useRef(null);

  const bride_name = weddingData?.bride_name || "Bride";
  const groom_name = weddingData?.groom_name || "Groom";
  const ceremony_type = weddingData?.ceremony_type || "wedding";

  const wedding_date = weddingData?.wedding_date || "12 December 2026";
  const wedding_venue =
    weddingData?.wedding_location || weddingData?.venue || "Colombo, Sri Lanka";

  const isTrial = weddingData?.isTrial || weddingData?.plan === "trial";

  const showInviteSection = isSectionVisible(weddingData, "isInviteSection");
  const showCardComponent = isSectionVisible(weddingData, "isCardComponent");
  const showCountDown = isSectionVisible(weddingData, "isCountDown");
  const isPremium = weddingData?.plan === "premium";
  const guestRsvpAddonEnabled = !isPremium || weddingData?.addons?.digitalInvite === true;

  const showRsvp =
    guestRsvpAddonEnabled && isSectionVisible(weddingData, "isRsvp");
  const showAgenda = isSectionVisible(weddingData, "isAgenda");
  const showLoveNote = isSectionVisible(weddingData, "isLoveNote");

  const themeStyle = useMemo(() => {
    return createThemeStyle(weddingData?.theme || {});
  }, [weddingData]);

  const shouldShowImages = useMemo(() => {
    return typeof weddingData?.showImages === "boolean"
      ? weddingData.showImages
      : true;
  }, [weddingData]);

  const canShowSeatingArrangement = useMemo(() => {
    if (isTrial) return true;
    return !!weddingData?.addons?.seatingArrangement;
  }, [isTrial, weddingData]);

  const bannerImage = useMemo(() => {
    const imageGallery = weddingData?.imageGallery;

    if (
      imageGallery &&
      typeof imageGallery === "object" &&
      !Array.isArray(imageGallery)
    ) {
      return (
        imageGallery?.signatureBannerImage ||
        imageGallery?.mainImage ||
        imageGallery?.bannerImage ||
        ""
      );
    }

    if (Array.isArray(imageGallery)) {
      return (
        imageGallery?.find((item) => item?.key === "signatureBannerImage")
          ?.image ||
        imageGallery?.find((item) => item?.key === "mainImage")?.image ||
        imageGallery?.[0]?.image ||
        ""
      );
    }

    return weddingData?.bannerImage || "";
  }, [weddingData]);

  const [loadedBanner, setLoadedBanner] = useState("");
  const [isBannerReady, setIsBannerReady] = useState(false);

  const storageKey = useMemo(
    () => `pmg_music_enabled_${slug || "default"}`,
    [slug]
  );

  const [isMusicEnabled, setIsMusicEnabled] = useState(true);
  const [musicError, setMusicError] = useState(false);
  const [autoPlayBlocked, setAutoPlayBlocked] = useState(false);

  const [showIntro, setShowIntro] = useState(true);
  const [isOpeningIntro, setIsOpeningIntro] = useState(false);
  const [shouldRenderSite, setShouldRenderSite] = useState(false);

  useEffect(() => {
    setLoadedBanner("");
    setIsBannerReady(false);

    if (!bannerImage) return;

    const img = new Image();
    img.src = bannerImage;

    const handleLoad = () => {
      setLoadedBanner(bannerImage);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsBannerReady(true);
        });
      });
    };

    img.onload = handleLoad;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [bannerImage]);

  useEffect(() => {
    try {
      const savedPreference = localStorage.getItem(storageKey);
      setIsMusicEnabled(savedPreference !== "false");
    } catch (error) {
      console.error("Music preference load failed:", error);
      setIsMusicEnabled(true);
    }
  }, [storageKey]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = 0.55;
    audio.loop = true;
    audio.preload = "auto";
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleAudioError = () => {
      setMusicError(true);
      setIsMusicEnabled(false);
      setAutoPlayBlocked(false);
    };

    audio.addEventListener("error", handleAudioError);

    return () => {
      audio.removeEventListener("error", handleAudioError);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = showIntro ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
      if (introTimerRef.current) {
        clearTimeout(introTimerRef.current);
      }
    };
  }, [showIntro]);

  const toggleMusic = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    const nextValue = !isMusicEnabled;
    setIsMusicEnabled(nextValue);

    try {
      localStorage.setItem(storageKey, String(nextValue));
    } catch (error) {
      console.error("Music preference save failed:", error);
    }

    if (nextValue) {
      try {
        await audio.play();
        setMusicError(false);
        setAutoPlayBlocked(false);
      } catch (error) {
        console.error("Audio play failed:", error);
        setAutoPlayBlocked(true);
      }
    } else {
      audio.pause();
      setAutoPlayBlocked(false);
    }
  };

  const handleOpenInvitation = async () => {
    if (isOpeningIntro) return;

    setShouldRenderSite(true);
    setIsOpeningIntro(true);

    const audio = audioRef.current;

    if (audio) {
      try {
        await audio.play();
        setIsMusicEnabled(true);
        setMusicError(false);
        setAutoPlayBlocked(false);

        try {
          localStorage.setItem(storageKey, "true");
        } catch (error) {
          console.error("Music preference save failed:", error);
        }
      } catch (error) {
        console.error("Audio play failed on open:", error);
        setAutoPlayBlocked(true);
      }
    }

    introTimerRef.current = setTimeout(() => {
      setShowIntro(false);
    }, 1500);
  };

  const scrollToSelector = (selector) => {
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToContent = () => {
    if (showInviteSection) {
      scrollToSelector(".inviteSection");
      return;
    }

    if (showCardComponent) {
      scrollToSelector(".home-cardSection");
      return;
    }

    if (showCountDown) {
      scrollToSelector(".home-countdownSection");
    }
  };

  const scrollToRsvp = () => {
    if (!showRsvp) return;
    scrollToSelector(".home-rsvpSection");
  };

  const scrollToSeat = () => {
    if (!canShowSeatingArrangement) return;
    scrollToSelector(".home-seatSection");
  };

  const musicSrc = weddingData?.musicUrl || weddingMusicFile;

  return (
    <div
      className={`home ${showIntro ? "is-intro-active" : "is-site-ready"}`}
      style={themeStyle}
    >
      <audio ref={audioRef} preload="auto" loop>
        <source src={musicSrc} type="audio/mpeg" />
      </audio>

      {showIntro && (
        <OpeningComponent
          brideName={bride_name}
          groomName={groom_name}
          weddingData={weddingData}
          isOpening={isOpeningIntro}
          onOpen={handleOpenInvitation}
        />
      )}

      {shouldRenderSite && (
        <>
          <div className="home-heroDecor" aria-hidden="true">
            {Array.from({ length: 22 }).map((_, index) => (
              <span
                key={index}
                className={`home-petal home-petal--${(index % 6) + 1}`}
                style={{
                  left: `${(index * 11) % 100}%`,
                  animationDelay: `${index * 0.55}s`,
                }}
              />
            ))}
          </div>

          <button
            type="button"
            className={`home-musicToggle ${isMusicEnabled ? "is-playing" : ""} ${musicError ? "is-error" : ""
              }`}
            onClick={toggleMusic}
            aria-label={
              isMusicEnabled ? "Pause wedding music" : "Play wedding music"
            }
            title={isMusicEnabled ? "Pause music" : "Play music"}
          >
            <span className="home-musicToggle__ring" />
            <span className="home-musicToggle__icon">
              {musicError ? (
                <FaVolumeMute />
              ) : isMusicEnabled ? (
                <FaPause />
              ) : (
                <FaMusic />
              )}
            </span>
          </button>

          <HeroSectionComponent
            brideName={bride_name}
            groomName={groom_name}
            ceremonyType={ceremony_type}
            weddingDate={wedding_date}
            weddingVenue={wedding_venue}
            loadedBanner={loadedBanner}
            isBannerReady={isBannerReady}
            canShowSeatingArrangement={canShowSeatingArrangement}
            isRsvp={showRsvp}
            onScrollToRsvp={scrollToRsvp}
            onScrollToSeat={scrollToSeat}
            onScrollToContent={scrollToContent}
          />

          <main className="home-content">
            {showInviteSection && (
              <InviteSectionComponent weddingData={weddingData} />
            )}

            {showCardComponent && (
              <section className="home-cardSection">
                <CardComponent
                  variant="dark"
                  slug={slug}
                  weddingData={weddingData}
                />
              </section>
            )}

            {showCountDown && (
              <section className="home-countdownSection">
                <CountDownComponent slug={slug} weddingData={weddingData} />
              </section>
            )}

            {showRsvp && (
              <section className="home-rsvpSection">
                <RsvpComponent slug={slug} weddingData={weddingData} />
              </section>
            )}

            {showAgenda && (
              <section className="home-agendaSection">
                <AgendaComponent slug={slug} weddingData={weddingData} />
              </section>
            )}

            {canShowSeatingArrangement && (
              <section className="home-seatSection">
                <TableSearch slug={slug} weddingData={weddingData} />
              </section>
            )}

            {shouldShowImages && (
              <section className="home-photoSection">
                <PhotoComponent weddingData={weddingData} />
              </section>
            )}

            {showLoveNote && (
              <section className="home-messageSection">
                <MessageToGuests slug={slug} weddingData={weddingData} />
              </section>
            )}

            {isTrial && (
              <div className="home-trialPanel">
                <ButtonComponent
                  onClick={() => navigate(`/sites/${slug}/dashboard`)}
                >
                  Go Admin Panel
                </ButtonComponent>
                <p className="pmg-trial-note-btn">
                  This button is only visible in the trial version
                </p>
              </div>
            )}

            <WeddingFooterComponent
              weddingData={weddingData}
              slug={slug}
              canShowSeatingArrangement={canShowSeatingArrangement}
              canShowRsvp={showRsvp}
            />
          </main>
        </>
      )}
    </div>
  );
};

export default WeddingHomeComponent;